/**
 * Terakkim gün satırları için hedef yardımcıları (mobil useAmelAgenda ve
 * buildDayGoals ile aynı kurallar): haftanın belli günlerine kurulmuş hedef
 * diğer günlerde görünmez; eski gün kayıtlarında olmayan birim ve biçim
 * kullanıcının güncel hedefinden tamamlanır.
 */
import { goalKey, isSameGoal } from "../app/goals/goalRules";

/** Hedef verilen günde geçerli mi (boş liste her gün). Saat dilimi kaymasın diye UTC. */
export function appliesOn(weekDays, localDate) {
  if (!Array.isArray(weekDays) || weekDays.length === 0) return true;
  const [year, month, day] = String(localDate || "").split("-").map(Number);
  if (!year || !month || !day) return true;
  return weekDays.includes(new Date(Date.UTC(year, month - 1, day)).getUTCDay());
}

/** Gün kaydındaki hedefe profildeki karşılığından birim, biçim ve günler eklenir. */
export function enrichGoal(goal, profileGoals) {
  const list = Array.isArray(profileGoals) ? profileGoals : [];
  const profile =
    list.find((p) => isSameGoal(p, goal.label, goal.activity)) ||
    (goal.activity ? null : list.find((p) => p.label === goal.label)) ||
    null;
  return {
    ...goal,
    unit: goal.unit ?? profile?.unit,
    activity: goal.activity ?? profile?.activity,
    weekDays: Array.isArray(goal.weekDays) ? goal.weekDays : Array.isArray(profile?.weekDays) ? profile.weekDays : [],
  };
}

/**
 * Seçili günde gösterilecek hedefler: o gün geçerli olmayanlar gizlenir;
 * ama o gün değer girilmişse (kayıt tutarlı kalsın) görünmeye devam eder.
 */
export function visibleDayGoals(goals, localDate, savedValues = {}) {
  return (goals || []).filter(
    (g) => appliesOn(g.weekDays, localDate) || goalValueIn(savedValues, g) > 0
  );
}

/** Değer haritasında hedefin değeri: ad + biçim anahtarı, eski haritada ad. */
export function goalValueIn(values, goal) {
  const key = goalKey(goal?.label, goal?.activity);
  const raw = values && key in values ? values[key] : values?.[goal?.label];
  return Number(raw) || 0;
}

/**
 * O günün hedefleri: geçmiş gün düzenlenirken bugünkü hedefler değil, o gün
 * geçerli olan snapshot esas alınır (hedef değeri ve biçimiyle). Snapshot
 * yoksa profildeki hedefler. Eksik birim ve biçim profilden tamamlanır.
 */
export function dayGoalsFor(entry, profileGoals) {
  const snapshot = entry?.goalDetailSnapshot;
  const source = Array.isArray(snapshot) && snapshot.length > 0 ? snapshot : profileGoals || [];
  return source.map((g) => enrichGoal(g, profileGoals));
}

/**
 * Günün kayıtlı değerleri, ad + biçim anahtarlı. Biçimi olan satır kendi
 * hedefine; biçimsiz eski satır aynı addaki ilk boş hedefe bağlanır.
 */
export function savedDayValues(goals, rows) {
  const list = Array.isArray(rows) ? rows : [];
  const used = new Set();
  const values = {};
  const keys = (goals || []).map((g) => goalKey(g.label, g.activity));
  keys.forEach((key) => {
    values[key] = 0;
  });
  list.forEach((row, index) => {
    if (!row?.activity) return;
    const key = goalKey(row.label, row.activity);
    if (key in values) {
      values[key] = Number(row.value) || 0;
      used.add(index);
    }
  });
  (goals || []).forEach((goal, i) => {
    const key = keys[i];
    if (list.some((row, index) => used.has(index) && goalKey(row.label, row.activity) === key)) return;
    const index = list.findIndex((row, j) => !used.has(j) && !row?.activity && row?.label === goal.label);
    if (index === -1) return;
    used.add(index);
    values[key] = Number(list[index].value) || 0;
  });
  return values;
}

/** Hedef yapıldı mı: hedefi 0 olan hedef yapılmış sayılmaz. */
export function isGoalDone(goal, value) {
  const target = Number(goal?.goalValue) || 0;
  return target > 0 && (Number(value) || 0) >= target;
}

/** Mükemmel gün: hedefi olan bütün hedefler tamam; hedefi 0 olanlar sayılmaz. */
export function isDayPerfect(goals, values) {
  const counted = (goals || []).filter((g) => (Number(g.goalValue) || 0) > 0);
  return counted.length > 0 && counted.every((g) => isGoalDone(g, goalValueIn(values, g)));
}

/** Kaydedilecek gün satırları: ad, biçim ve birimle (sunucu hedefi tam eşlesin). */
export function dayLogRows(goals, values) {
  return (goals || []).map((g) => ({
    label: g.label,
    activity: g.activity,
    unit: g.unit,
    value: goalValueIn(values, g),
    goalValue: g.goalValue,
    isCustom: g.isCustom !== false,
  }));
}

/**
 * Odaklan penceresinde seçilebilen hedefler: yalnız dakika birimli ve
 * (gün verildiyse) o gün geçerli olanlar.
 */
export function minuteGoals(profileGoals, localDate) {
  return (Array.isArray(profileGoals) ? profileGoals : [])
    .filter((g) => g && g.unit === "minute" && (!localDate || appliesOn(g.weekDays, localDate)))
    .map((g) => ({
      key: goalKey(g.label, g.activity),
      label: g.label,
      activity: g.activity,
      goalValue: Number(g.goalValue) || 0,
    }));
}
